import React, { useEffect, useState } from 'react';
import { Drawer, Box, IconButton, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Typography, Button, Input } from '@/components/ui';

export interface RoleFormValues {
  id?: string;
  name: string;
}

export interface FormErrors {
  name?: string;
}

export interface RoleFormDrawerProps {
  open: boolean;
  initialValues?: RoleFormValues | null;
  onClose: () => void;
  onSubmit: (values: RoleFormValues) => void;
}

const drawerWidth = 420;

// Validation helper function
const validateRoleName = (name: string): string | undefined => {
  if (!name.trim()) {
    return 'Role name is required';
  }
  if (name.trim().length > 20) {
    return 'Role name must not exceed 20 characters';
  }
  return undefined;
};

const RoleFormDrawer: React.FC<RoleFormDrawerProps> = ({ open, initialValues, onClose, onSubmit }) => {
  const [values, setValues] = useState<RoleFormValues>({ name: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const isEdit = Boolean(initialValues?.id);

  useEffect(() => {
    if (initialValues) {
      setValues({ id: initialValues.id, name: initialValues.name || '' });
    } else {
      setValues({ name: '' });
    }
    // Clear errors when form resets
    setErrors({});
  }, [initialValues]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setValues(prev => ({ ...prev, name: value }));

    // Real-time validation
    const error = validateRoleName(value);
    setErrors(prev => ({
      ...prev,
      name: error,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate role name
    const nameError = validateRoleName(values.name);
    
    const newErrors: FormErrors = {
      name: nameError,
    };
    
    setErrors(newErrors);
    
    // Check if there are any errors
    const hasErrors = Object.values(newErrors).some(error => error !== undefined);
    
    if (hasErrors) {
      return; // Don't submit if there are validation errors
    }
    
    onSubmit({ ...values, name: values.name.trim() });
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  return (
    <Drawer anchor="right" open={open} onClose={handleClose} PaperProps={{ sx: { width: drawerWidth } }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={2}>
        <Typography variant="h6">{isEdit ? 'Edit Role' : 'Create Role'}</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <Box component="form" onSubmit={handleSubmit} px={2} py={2} display="flex" flexDirection="column" gap={2}>
        <Typography variant="subtitle1" fontWeight="medium">
          Role Details
        </Typography>
        <Input
          label="Role Name"
          placeholder="Enter role name"
          value={values.name}
          onChange={handleChange}
          fullWidth
          // required
          error={!!errors.name}
          helperText={errors.name}
          inputProps={{ maxLength: 20 }}
        />

        <Box display="flex" gap={1} mt={1}>
          <Button type="submit" variant="primary">
            {isEdit ? 'Update' : 'Create'}
          </Button>
          <Button type="button" variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default RoleFormDrawer;


